import { useEffect, useState } from "react";
import { Alert, Checkbox, CircularProgress, IconButton, Radio } from "@mui/material";
import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import { FaPlus, FaTrash, FaCheck, FaEdit, FaClipboardList, FaEllipsisV, FaLock } from "react-icons/fa";

import useCommon from "hooks/useCommon";
import usePermissions from "hooks/usePermissions";
import dayjs from "dayjs";
import HighlightedText from "./HighlightedText";
import { initializeWorkspace, getSectionValue as getDefaultSectionValue, buildEntrySections, isEntryEditable } from "../entryValues";

const { dark, gradients, light } = colors;

const OperationsWorkspace = ({
  template,
  searchQuery = "",
  workspace,
  onSave,
  onDelete,

  onEdit,
  selectedDate = dayjs().format("YYYY-MM-DD"),
  loading = false,
  error = null,
}) => {
  const { STORE } = useCommon();
  const { can } = usePermissions();

  const { operationsEntry, entryLoading, entryError, saveEntryLoading } = STORE(
    (state) => state.operations
  );

  const hotelIds = STORE((state) => state.ui.hotelIds);
  const canUpdateEntry = can("editOperations") && isEntryEditable(operationsEntry) && !entryLoading && !saveEntryLoading;

  const schema = operationsEntry?.snapshot?.snapshotData;
  const entry = operationsEntry?.entry;

  const [workspaceData, setWorkspaceData] = useState({});
  const [editingSections, setEditingSections] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleOpenMenu = (event) => {
    if (entryLoading || saveEntryLoading) return;

    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    if (entryLoading || saveEntryLoading) return;

    setMenuAnchor(null);
  };

  useEffect(() => {
    setWorkspaceData(initializeWorkspace(schema, entry));
    setEditingSections({});
  }, [operationsEntry, schema, entry]);

  const getSectionKey = (section) => section.sectionId;

  const isEditing = (section) => {
    const key = getSectionKey(section);
    return !!editingSections[key];
  };

  const toggleSectionEdit = (section) => {
    if (!canUpdateEntry) return;
    const key = getSectionKey(section);

    setEditingSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getSectionValue = (section) => {
    const key = getSectionKey(section);

    return workspaceData[key] || getDefaultSectionValue(section);
  };

  const updateSectionValue = (section, nextValue) => {
    if (!canUpdateEntry) return;
    const key = getSectionKey(section);

    setWorkspaceData((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
  };

  const updateChecklistItem = (section, optionKey, checked) => {
    const current = getSectionValue(section);

    updateSectionValue(section, {
      items: (current.items || []).map((item) =>
        item.itemId === optionKey ? { ...item, completed: checked } : item
      ),
    });
  };

  const addBulletItem = (section) => {
    const current = getSectionValue(section);

    updateSectionValue(section, {
      items: [...(current.items || []), { text: "" }],
    });
  };

  const updateBulletItem = (section, index, value) => {
    const current = getSectionValue(section);

    updateSectionValue(section, {
      items: (current.items || []).map((item, i) =>
        i === index ? { ...item, text: value } : item
      ),
    });
  };

  const removeBulletItem = (section, index) => {
    const current = getSectionValue(section);

    updateSectionValue(section, {
      items: (current.items || []).filter((_, i) => i !== index),
    });
  };

  const renderEmptyText = (text) => <VuiTypography variant="caption">{text}</VuiTypography>;

  const renderTextField = (section, editable, props = {}) => {
    const current = getSectionValue(section);
    const value = current.value || "";

    if (!editable) {
      let displayValue = value;

      if (section.fieldType === "date_field" && value) {
        displayValue = dayjs(value).format("DD/MM/YYYY");
      }

      return displayValue ? (
        <VuiTypography
          component="div"
          variant="button"
          color="primary"
          fontWeight="regular"
          sx={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.35,
          }}
        >
          <HighlightedText text={displayValue} query={searchQuery} />
        </VuiTypography>
      ) : (
        renderEmptyText("No information added yet.")
      );
    }

    return (
      <VuiInput
        gradient
        fullWidth
        value={value}
        onChange={(e) =>
          updateSectionValue(section, {
            value: e.target.value,
          })
        }
        {...props}
      />
    );
  };

  const renderCheckboxList = (section) => {
    const current = getSectionValue(section);
    const options = section.fieldConfig?.options || [];
    if (!options.length) return renderEmptyText("No checklist options configured.");
    return (
      <VuiBox display="flex" flexDirection="column" gap={1}>
        {options.map((option) => {
          const checked = !!current.items?.find((item) => item.itemId === option.key)?.completed;
          return (
            <VuiBox key={option.key} component="label" display="flex" alignItems="center" gap={1}>
              <Checkbox checked={checked} disabled={!canUpdateEntry}
                onChange={(e) => updateChecklistItem(section, option.key, e.target.checked)}
                inputProps={{ "aria-label": option.label }} sx={{ color: dark.main }} />
              <VuiTypography variant="button" color="primary" fontWeight="regular"
                sx={{ textDecoration: checked ? "line-through" : "none" }}>
                <HighlightedText text={option.label} query={searchQuery} />
              </VuiTypography>
            </VuiBox>
          );
        })}
      </VuiBox>
    );
  };

  const renderBulletList = (section, editable) => {
    const current = getSectionValue(section);
    const items = current.items || [];
    const visibleItems = items.filter((item) => item.text?.trim());

    if (!editable) {
      if (!visibleItems.length) {
        return renderEmptyText("No list items added yet.");
      }

      return (
        <VuiBox display="flex" flexDirection="column" gap={1}>
          {visibleItems.map((item, index) => (
            <VuiBox key={index} display="flex" gap={1}>
              <VuiTypography variant="button">•</VuiTypography>

              <VuiTypography variant="button" color="primary" fontWeight="regular">
                <HighlightedText text={item.text || "Untitled list item"} query={searchQuery} />
              </VuiTypography>
            </VuiBox>
          ))}
        </VuiBox>
      );
    }

    return (
      <VuiBox display="flex" flexDirection="column" gap={1}>
        {!items.length &&
          renderEmptyText("No list items yet. Add the first item for this section.")}

        {items.map((item, index) => (
          <VuiBox key={index} display="flex" alignItems="center" gap={1}>
            <VuiTypography variant="button">•</VuiTypography>

            <VuiInput
              gradient
              fullWidth
              placeholder="List item..."
              inputProps={{ maxLength: 500 }}
              value={item.text || ""}
              onChange={(e) => updateBulletItem(section, index, e.target.value)}
            />

            <IconButton size="small" onClick={() => removeBulletItem(section, index)}>
              <FaTrash size={12} color="#ef4444" />
            </IconButton>
          </VuiBox>
        ))}

        <VuiBox mt={1}>
          <VuiButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => addBulletItem(section)}
          >
            <FaPlus size={10} style={{ marginRight: 6 }} />
            Add List Item
          </VuiButton>
        </VuiBox>
      </VuiBox>
    );
  };

  const renderRadioButtons = (section) => {
    const current = getSectionValue(section);
    const value = current.value || "";
    const options = section.fieldConfig?.options || [];

    if (!options.length) {
      return renderEmptyText("No radio options configured.");
    }

    return (
      <VuiBox display="flex" flexDirection="column" gap={1}>
        {options.map((option) => {
          const checked = value === option.key;

          return (
            <VuiBox
              key={option.key}
              component="label"
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ cursor: canUpdateEntry ? "pointer" : "default" }}
            >
              <Radio
                checked={checked}
                disabled={!canUpdateEntry}
                name={`operations-${section.sectionId}`}
                value={option.key}
                inputProps={{ "aria-label": option.label }}
                onChange={() =>
                  updateSectionValue(section, {
                    value: option.key,
                  })
                }
                sx={{
                  color: dark.main,
                }}
              />

              <VuiTypography variant="button" fontWeight="regular" color="primary">
                <HighlightedText text={option.label} query={searchQuery} />
              </VuiTypography>
            </VuiBox>
          );
        })}
      </VuiBox>
    );
  };

  const renderField = (section) => {
    const editable = canUpdateEntry && isEditing(section);

    switch (section.fieldType) {
      case "checkbox_list":
        return renderCheckboxList(section, editable);

      case "bullet_point_list":
        return renderBulletList(section, editable);

      case "radio_buttons":
        return renderRadioButtons(section);

      case "text_input":
        return renderTextField(section, editable, {
          placeholder: "Enter information...",
        });

      case "text_area":
        return renderTextField(section, editable, {
          multiline: true,
          rows: section.title?.toLowerCase()?.includes("pass down") ? 9 : 6,
          placeholder: "Enter notes...",
        });

      case "date_field":
        return renderTextField(section, editable, {
          type: "date",
        });

      case "time_field":
        return renderTextField(section, editable, {
          type: "time",
        });

      default:
        return renderTextField(section, editable, {
          multiline: true,
          rows: 6,
          placeholder: "Enter details...",
        });
    }
  };

  const handleSave = () => {
    if (!canUpdateEntry) return;
    const payload = {
      hotelId: hotelIds?.[0],
      templateId: template?.value,
      operationDate: selectedDate,
      sections: buildEntrySections(schema, workspaceData),
    };

    onSave?.(payload);
  };

  if (entryLoading) {
    return (
      <VuiBox py={10} display="flex" justifyContent="center">
        <CircularProgress color="primary" />
      </VuiBox>
    );
  }

  if (entryError) {
    return (
      <VuiBox py={4}>
        <Alert severity="error">{entryError?.message || entryError}</Alert>
      </VuiBox>
    );
  }

  if (!schema || !operationsEntry) {
    return (
      <VuiBox
        py={10}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <VuiBox
          width="80px"
          height="80px"
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          mb={3}
          sx={{
            background:
              "linear-gradient(135deg, rgba(0,117,255,0.15) 0%, rgba(94,114,228,0.15) 100%)",
            border: "1px solid rgba(94,114,228,0.25)",
          }}
        >
          <FaClipboardList size={32} color="#5E72E4" />
        </VuiBox>

        <VuiTypography variant="h4" fontWeight="bold" textAlign="center" mb={1}>
          No Data Available
        </VuiTypography>

        <VuiTypography variant="caption" textAlign="center" maxWidth="600px">
          No operations workspace exists for the selected date. This can happen if no template
          snapshot was available for that day.
        </VuiTypography>
      </VuiBox>
    );
  }

  const sortedSections = [...(schema?.sections || [])].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
  );

  return (
    <VuiBox mt={1}>
      {operationsEntry.mode === "view" && (
        <VuiBox
          role="status"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            px: 1.5,
            py: 0.75,
            borderRadius: "12px",
            backgroundColor: rgba(dark.main, 0.04),
            border: `1px solid ${rgba(dark.main, 0.14)}`,
          }}
        >
          <VuiBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              flexShrink: 0,
              borderRadius: "50%",
              backgroundColor: rgba(dark.main, 0.08),
              color: dark.main,
            }}
          >
            <FaLock size={12} aria-hidden="true" />
          </VuiBox>
          <VuiBox sx={{ minWidth: 0 }}>
            <VuiTypography component="div" color="dark" fontWeight="medium" sx={{ fontSize: 13, lineHeight: 1.4 }}>
              Read-only entry
            </VuiTypography>
            <VuiTypography component="div" color="dark" sx={{ fontSize: 12, lineHeight: 1.5, opacity: 0.75, mt: 0.25 }}>
              You can review this entry, but changes are disabled.
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      )}
      <VuiBox
        p={{ xs: 2, md: 2 }}
        borderRadius="xl"
        sx={{
          background: "rgba(255,255,255,0.03)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: `
            0 6px 20px rgba(0,0,0,0.25),
            inset 0 1px 0 rgba(255,255,255,0.08)
          `,
        }}
      >
        {/* <VuiBox textAlign="center" mb={2}>
          <VuiTypography variant="h4"  fontWeight="medium">
            {schema.workspaceHeading}
          </VuiTypography>

          {schema.description && (
            <VuiTypography variant="caption"  display="block" mt={1}>
              {schema.description}
            </VuiTypography>
          )}

          <VuiTypography variant="body2" sx={{ fontSize: "14px" }}  mt={0.5}>
            {new Date(selectedDate).toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </VuiTypography>
        </VuiBox> */}

        <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <VuiBox flex={1} />

          <VuiBox textAlign="center" flex={1}>
            <VuiTypography variant="h4" fontWeight="medium">
              <HighlightedText text={schema.workspaceHeading} query={searchQuery} />
            </VuiTypography>

            {schema.description && (
              <VuiTypography variant="caption" display="block" mt={1}>
                <HighlightedText text={schema.description} query={searchQuery} />
              </VuiTypography>
            )}

            <VuiTypography variant="body2" sx={{ fontSize: "14px" }} mt={0.5}>
              {dayjs(selectedDate).format("dddd, MMMM D, YYYY")}
            </VuiTypography>
          </VuiBox>

          <VuiBox flex={1} display="flex" justifyContent="flex-end">
            {(can("editOperations") || can("deleteOperations")) && (
              <IconButton onClick={handleOpenMenu}>
                <FaEllipsisV size={14} />
              </IconButton>
            )}

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleCloseMenu}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: "14px",
                  minWidth: 240,

                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",

                  border: "1px solid rgba(255, 255, 255, 0.18)",
                  borderRadius: "16px",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.35)",

                  // color: "#fff",
                },
              }}
            >
              {can("editOperations") && (
                <MenuItem
                  dense
                  onClick={() => {
                    handleCloseMenu();
                    onEdit?.();
                  }}
                  sx={{
                    py: 1,
                    px: 1.5,
                  }}
                >
                  <ListItemIcon>
                    <FaEdit size={12} />
                  </ListItemIcon>

                  <ListItemText
                    primary="Edit Template"
                    primaryTypographyProps={{
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
              )}

              {can("deleteOperations") && (
                <MenuItem
                  dense
                  onClick={() => {
                    handleCloseMenu();
                    onDelete?.();
                  }}
                  sx={{
                    py: 1,
                    px: 1.5,
                  }}
                >
                  <ListItemIcon>
                    <FaTrash size={12} color="#ef4444" />
                  </ListItemIcon>

                  <ListItemText
                    primary="Delete Template"
                    primaryTypographyProps={{
                      color: "#ef4444",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
              )}
            </Menu>
          </VuiBox>
        </VuiBox>

        <VuiBox
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            lg: "1fr 1fr",
          }}
          gap={2}
        >
          {sortedSections.map((section) => {
            const editable = canUpdateEntry && isEditing(section);

            return (
              <VuiBox
                key={getSectionKey(section)}
                // onClick={() => toggleSectionEdit(section)}
                sx={{
                  minWidth: 0,
                  overflowWrap: "anywhere",
                  "& .MuiTypography-root": {
                    minWidth: 0,
                    overflowWrap: "anywhere",
                  },
                  height: "100%",
                  p: 1.5,
                  borderRadius: "12px",
                  border: `${1}px solid ${rgba(dark.main, 0.25)}`,
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  transition: "all .2s ease",
                  display: "flex",
                  flexDirection: "column",

                  "&:hover": {
                    borderColor: dark.main,
                    transform: "translateY(-2px)",
                  },

                  gridColumn: section.title?.toLowerCase()?.includes("pass down")
                    ? "1 / -1"
                    : "auto",
                }}
              >
                <VuiBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={1}
                  mb={2}
                >
                  <VuiBox sx={{ minWidth: 0 }}>
                    <VuiTypography
                      variant="button"
                      fontWeight="bold"
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        fontSize: "15px",
                      }}
                    >
                      <HighlightedText text={section.title} query={searchQuery} />
                    </VuiTypography>

                    {section.subheading && (
                      <VuiTypography
                        variant="caption"
                        display="block"
                        sx={{
                          fontSize: "12px",
                        }}
                      >
                        <HighlightedText text={section.subheading} query={searchQuery} />
                      </VuiTypography>
                    )}
                  </VuiBox>

                  {canUpdateEntry && !["checkbox_list", "radio_buttons"].includes(section.fieldType) && (
                    <VuiButton
                      disabled={entryLoading || saveEntryLoading}
                      color={editable ? "success" : "primary"}
                      variant={editable ? "contained" : "outlined"}
                      size="small"
                      onClick={() => toggleSectionEdit(section)}
                      sx={{
                        minWidth: editable ? "80px" : "34px",
                        flexShrink: 0,
                        height: "26px",
                        borderRadius: "10px",
                        px: editable ? 1.5 : 1,
                      }}
                    >
                      {editable ? (
                        <>
                          <FaCheck size={11} style={{ marginRight: 6 }} />
                          Done
                        </>
                      ) : (
                        <FaEdit size={12} />
                      )}
                    </VuiButton>
                  )}
                </VuiBox>

                <VuiBox
                  sx={{
                    flex: 1,
                    minHeight: section.title?.toLowerCase()?.includes("pass down")
                      ? "120px"
                      : "100px",
                  }}
                >
                  {renderField(section)}
                </VuiBox>
              </VuiBox>
            );
          })}
        </VuiBox>

        <VuiBox mt={5} display="flex" justifyContent="flex-end" gap={2}>
          {isEntryEditable(operationsEntry) && can("editOperations") && (
            <VuiButton color="primary" onClick={handleSave} disabled={!canUpdateEntry}>
              {saveEntryLoading ? <CircularProgress color="light" size={20} /> : "Save Changes"}
            </VuiButton>
          )}
        </VuiBox>
      </VuiBox>
    </VuiBox>
  );
};

export default OperationsWorkspace;
